import { useEffect, useState } from "react";
import { authAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Link2, Copy, Check } from "lucide-react";

export default function ProfileSlugCard() {
  const { user, setUser } = useAuth();
  const [slug, setSlug] = useState(user?.profile_slug || "");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    try {
      const r = await authAPI.getSlugStatus();
      setStatus(r.data);
      setSlug(r.data.slug || "");
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const canChange = !!status?.can_change_now;

  const save = async (e) => {
    e.preventDefault();
    const cleaned = slug.trim().toLowerCase();
    if (!cleaned) {
      toast.error("Enter a URL slug");
      return;
    }
    setSaving(true);
    try {
      const r = await authAPI.updateSlug(cleaned);
      setUser(r.data);
      localStorage.setItem("user", JSON.stringify(r.data));
      toast.success("Profile URL updated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update URL");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async () => {
    if (!status?.slug) return;
    try {
      await navigator.clipboard.writeText(`${origin}/u/${status.slug}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Card className="border-border bg-card p-5" data-testid="profile-slug-card">
      <div className="mb-1 flex items-center gap-2">
        <Link2 className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg font-semibold uppercase">Profile URL</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Claim a custom URL for your profile. You can change this once every {status?.cooldown_days ?? 30} days.
      </p>

      {status?.slug && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm">
          <code className="flex-1 truncate font-mono text-xs" data-testid="profile-slug-current-url">
            {origin}/u/{status.slug}
          </code>
          <button type="button" onClick={copyUrl} className="text-primary hover:text-primary/80" data-testid="profile-slug-copy">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      )}

      <form onSubmit={save} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="profile-slug">Custom URL</Label>
          <div className="flex items-stretch overflow-hidden rounded-md border border-border bg-background">
            <span className="flex items-center px-3 text-xs font-mono text-muted-foreground border-r border-border">/u/</span>
            <Input
              id="profile-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 30))}
              placeholder="your-name"
              disabled={!canChange || saving}
              className="border-0 focus-visible:ring-0"
              data-testid="profile-slug-input"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">3-30 chars · lowercase letters, numbers, hyphens, underscores.</p>
        </div>

        {!canChange && status?.days_remaining > 0 && (
          <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid="profile-slug-locked">
            You can change your URL again in <strong>{status.days_remaining} day(s)</strong>. Contact an admin if you need it changed sooner.
          </p>
        )}

        <Button
          type="submit"
          disabled={saving || !canChange || !slug || slug === status?.slug}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="profile-slug-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (status?.slug ? "Update URL" : "Claim URL")}
        </Button>
      </form>
    </Card>
  );
}
